import safe from 'app/common/lib/util/safe';
import * as locale from 'locale';
import React, { ReactNode } from 'react';
import {
  AllSubstitutions,
  AllSubstitutionValues,
  makeVisitor,
  NestedStrings,
  ReactSubstitutions,
  StringNotFoundAtPathError,
  StringSubstitutions,
  visit,
} from './babble';

const rawStrings = {
  ...locale.strings,
  axis: locale.axis,
  exports: locale.exports,
  labels: locale.labels,
  billing: locale.billing,
  social: locale.social,
  actions: locale.actions,
  notifications: locale.notifications,
  notificationsGrouped: locale.notificationsGrouped,
};

const templateStrings = {
  ...locale.templates,
};

const entityStrings = {
  action: locale.actions,
  notification: locale.notifications,
  notificationsGrouped: locale.notificationsGrouped,
};

class SubstitutionNotFound extends Error {}

const visitor = makeVisitor(
  (literal) => literal.value,
  (hole, substitutions, shouldEscapeStrings) => {
    const key = hole.key;
    if (
      !substitutions ||
      !Object.prototype.hasOwnProperty.call(substitutions, key)
    ) {
      throw new SubstitutionNotFound(
        `Substitution for ${key} but it wasn't provided`,
      );
    }

    const value = substitutions[key] as string | ReactNode;

    if (typeof value === 'string' && shouldEscapeStrings) {
      return safe(value);
    } else {
      return value;
    }
  },
);

function isReactSubstitutions(
  substitutions?: AllSubstitutions,
): substitutions is ReactSubstitutions {
  return (
    !!substitutions &&
    Object.keys(substitutions).some((key) =>
      React.isValidElement(substitutions[key]),
    )
  );
}

function makeFormat(
  namespace: string[] = [],
  strings: NestedStrings,
  fileName: string,
  shouldEscapeStrings: boolean = true,
  returnBlankForMissingStrings: boolean = false,
) {
  function format(
    keyPath: string | string[],
    substitutions?: StringSubstitutions,
  ): string;
  function format(
    keyPath: string | string[],
    substitutions: ReactSubstitutions,
  ): AllSubstitutionValues[];
  function format(
    keyPath: string | string[],
    substitutions?: AllSubstitutions,
  ): string | AllSubstitutionValues[] {
    const path = Array.isArray(keyPath)
      ? [...namespace, ...keyPath]
      : [...namespace, keyPath];

    try {
      const visited = visit(
        strings,
        path,
        visitor,
        substitutions,
        shouldEscapeStrings,
      );
      if (isReactSubstitutions(substitutions)) {
        return Array.isArray(visited) ? visited : [];
      } else {
        return Array.isArray(visited) ? visited.join('') : '';
      }
    } catch (e) {
      const printablePath = safe(path.join('.'));
      if (e instanceof StringNotFoundAtPathError) {
        if (returnBlankForMissingStrings) {
          return '';
        }
        console.error(
          `Unable to find localized string at path: ${printablePath}, check ${fileName}`,
        );
      } else if (e instanceof SubstitutionNotFound) {
        console.error(
          `Substitution missing when building localizes string at path ${printablePath}, ${e.message}`,
        );
      } else {
        console.error(
          `Unknown error thrown while attempting to localize string: ${e}`,
        );
      }

      return [printablePath];
    }
  }

  return format;
}

interface Options {
  shouldEscapeStrings?: boolean;
  returnBlankForMissingStrings?: boolean;
}

export const forTemplate = (
  namespace: string,
  {
    shouldEscapeStrings = true,
    returnBlankForMissingStrings = false,
  }: Options = {},
) =>
  makeFormat(
    [namespace],
    templateStrings,
    'templates/en.json',
    shouldEscapeStrings,
    returnBlankForMissingStrings,
  );
export const forEntities = (
  namespace: string,
  { shouldEscapeStrings = true }: Options = {},
) =>
  makeFormat(
    [namespace],
    entityStrings,
    `en.json in ${namespace}`,
    shouldEscapeStrings,
  );
export const forNamespace = (
  ns: string | string[] = [],
  { shouldEscapeStrings = true }: Options = {},
) =>
  makeFormat(
    Array.isArray(ns) ? ns : [ns],
    rawStrings,
    'en.json in strings files',
    shouldEscapeStrings,
  );

const formatCounts = forNamespace('counts');
const { counting } = locale;

export const localizeCount = (
  name: string,
  count: number,
  substitutions: AllSubstitutions = {},
) => {
  return formatCounts([name, counting(count)], {
    count: `${count}`,
    ...substitutions,
  });
};

export { asMoney, asNumber } from './formatters';
